import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Drawer } from './ui/Drawer';
import { CURRENCIES } from '../lib/catalogs';
import { useApInvoices } from '../hooks/useApInvoices';
import { api } from '../lib/api';
import { formatAmount, formatDate } from '../lib/format';

type Rail = 'zengin' | 'swift' | 'ach' | 'sepa' | 'wise' | 'manual';

const inputCn =
  'w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
  'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label className="text-[11px] uppercase tracking-wide text-ink-500">{label}</label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

export function PaymentRunCreateDrawer({
  wsId,
  open,
  onOpenChange,
}: {
  wsId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [rail, setRail] = useState<Rail>('zengin');
  const [baseCurrency, setBaseCurrency] = useState('JPY');
  const [scheduledFor, setScheduledFor] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Only payable AP invoices need to appear — approved/scheduled. Filtering
  // here keeps the picker short instead of dumping the whole history.
  const approved = useApInvoices(wsId, { status: 'approved' });

  const create = useMutation({
    mutationFn: async () =>
      (await api.post(`/workspaces/${wsId}/payment-runs`, {
        rail,
        baseCurrency,
        scheduledFor: scheduledFor || undefined,
        apInvoiceIds: Array.from(selectedIds),
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment-runs', wsId] });
      setSelectedIds(new Set());
      onOpenChange(false);
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? 'Create failed'),
  });

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('payments.createRun')}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              create.mutate();
            }}
            disabled={create.isPending}
            className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
          >
            {t('common.save')}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        {error && (
          <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {error}
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <FormField label={t('payments.rail')}>
            <select value={rail} onChange={(e) => setRail(e.target.value as Rail)} className={inputCn}>
              <option value="zengin">Zengin (JP)</option>
              <option value="swift">SWIFT MT103</option>
              <option value="ach">ACH (US)</option>
              <option value="sepa">SEPA</option>
              <option value="wise">Wise</option>
              <option value="manual">Manual</option>
            </select>
          </FormField>
          <FormField label={t('payments.baseCurrency')}>
            <select
              value={baseCurrency}
              onChange={(e) => setBaseCurrency(e.target.value)}
              className={inputCn}
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={t('payments.scheduledFor')}>
            <input
              type="date"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
              className={inputCn}
            />
          </FormField>
        </div>

        <section>
          <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
            {t('payments.includeInvoices')}{' '}
            <span className="text-ink-400">({selectedIds.size} {t('common.selected')})</span>
          </h3>
          {approved.isLoading ? (
            <div className="text-sm text-ink-500 py-6 text-center">{t('common.loading')}</div>
          ) : (approved.data ?? []).length === 0 ? (
            <div className="text-sm text-ink-500 py-6 text-center rounded-lg border border-dashed border-ink-200 dark:border-ink-800">
              {t('payments.noApproved')}
            </div>
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800 rounded-lg border border-ink-200 dark:border-ink-800 max-h-72 overflow-y-auto">
              {(approved.data ?? []).map((inv) => {
                const checked = selectedIds.has(inv.id);
                return (
                  <li key={inv.id}>
                    <label className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-ink-50 dark:hover:bg-ink-900 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const next = new Set(selectedIds);
                          if (e.target.checked) next.add(inv.id);
                          else next.delete(inv.id);
                          setSelectedIds(next);
                        }}
                        className="h-4 w-4 rounded border-ink-300 dark:border-ink-700 accent-accent-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{inv.invoice_number ?? t('inbox.noInvoiceNumber')}</div>
                        <div className="text-xs text-ink-500">{formatDate(inv.due_date, i18n.language)}</div>
                      </div>
                      <span className="num">{formatAmount(inv.total_cents, inv.currency, i18n.language)}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </Drawer>
  );
}
